import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChapterEmblem } from "@/lib/brand";
import LockedChapter from "@/components/LockedChapter";
import LooxsLogo from "@/components/LooxsLogo";
import ProductImageTile from "@/components/ProductImageTile";
import WaitlistButton from "@/components/WaitlistButton";
import ChapterHeroReel from "@/components/ChapterHeroReel";
import PageMeta from "@/components/PageMeta";
import { ArrowUpRight, Lock } from "lucide-react";

const FOUNDER_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_looxs-ecommerce/artifacts/p0auqnyq_30BA266F-4E88-4859-BD2C-64C6C4891FCF.jpeg";

export default function Home() {
  const [chapters, setChapters] = useState([]);
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [productsByChapter, setProductsByChapter] = useState({});

  useEffect(() => {
    api.get("/chapters").then(r => setChapters(r.data));
    api.get("/products", { params: { limit: 6 } }).then(r => setProducts(r.data.slice(0, 6)));
    api.get("/campaigns").then(r => setCampaigns(r.data));
    api.get("/products").then(r => {
      const g = {}; for (const p of r.data) if (p.chapter_roman) (g[p.chapter_roman] ||= []).push(p);
      setProductsByChapter(g);
    });
  }, []);

  return (
    <div>
      <PageMeta
        title="London Luxury Fashion · Crafted in India"
        description="LOOXS is a private universe of contemporary luxury structured across eleven chapters — denim, tailoring, footwear, leather goods and eyewear. Designed in London, crafted in India."
      />
      {/* HERO — slow-fade reel of the eleven Him × Her chapter portraits behind the LOOXS logo. */}
      <section className="relative h-screen w-full overflow-hidden text-ivory" style={{ background: "#2b1f14" }}>
        <ChapterHeroReel chapters={chapters} />
        <div className="grain opacity-30" />
        {/* Visually hidden H1 — provides SEO structure without disrupting the editorial hero. */}
        <h1 className="sr-only">LOOXS — Contemporary luxury fashion for men and women, designed in London and crafted in India</h1>
        <div className="relative h-full flex flex-col justify-between px-6 md:px-16 pt-32 pb-16">
          <div className="text-center animate-fade-up" style={{ animationDelay: "300ms" }}>
            <p className="text-[10px] tracking-luxury uppercase text-gold">11 Categories · One Identity · London Wear</p>
          </div>

          <div className="flex flex-col items-center text-center">
            <div className="animate-fade-up" style={{ animationDelay: "200ms" }}>
              <LooxsLogo height={220} tone="ivory" />
            </div>
            <p className="mt-12 font-editorial-italic text-2xl md:text-4xl text-ivory/95 drop-shadow-lg animate-fade-up" style={{ animationDelay: "600ms" }}>
              Fashion from London.<br className="md:hidden" /> Crafted in India.
            </p>
            <div className="mt-14 animate-fade-up" style={{ animationDelay: "800ms" }}>
              <Link data-testid="hero-cta" to="/world" className="inline-flex items-center gap-3 tracking-widest-plus text-[11px] uppercase border-b border-ivory/70 pb-2 text-ivory hover:text-gold hover:border-gold transition-colors duration-500">
                Enter the world of LOOXS <ArrowUpRight className="w-4 h-4" strokeWidth={1} />
              </Link>
            </div>
          </div>

          <div className="flex justify-between items-end text-[10px] tracking-widest-plus uppercase text-ivory/70 animate-fade-in" style={{ animationDelay: "1000ms" }}>
            <span>XI Chapters</span>
            <span className="hidden md:block">Scroll to discover</span>
            <span>A Style in Flicks</span>
          </div>
        </div>
      </section>

      {/* MARQUEE strip */}
      <section className="section-dark border-y border-ivory/10 py-4 overflow-hidden">
        <div className="marquee-track whitespace-nowrap text-[10px] tracking-luxury uppercase text-ivory/60">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-16">
              <span>Fashion from London</span><span className="text-gold">◆</span>
              <span>Crafted in India</span><span className="text-gold">◆</span>
              <span>XI Chapters</span><span className="text-gold">◆</span>
              <span>Limited releases</span><span className="text-gold">◆</span>
              <span>Private appointments</span><span className="text-gold">◆</span>
              <span>A Style in Flicks</span><span className="text-gold">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* BRAND STATEMENT */}
      <section className="bg-ivory text-ink py-32 md:py-48 relative">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-8">The Ethos</p>
          <h2 className="font-editorial text-4xl md:text-7xl leading-[1.05] tracking-tight">
            Not just clothing.<br />
            <em className="font-editorial-italic text-gold/80">A language of identity.</em>
          </h2>
          <div className="rule-gold w-24 mx-auto my-16" />
          <p className="max-w-2xl mx-auto text-lg md:text-xl leading-relaxed text-ink/75 font-light">
            LOOXS is a private universe of contemporary luxury, structured across eleven chapters. Every garment carries a numeral. Every collection extends a story. Fashion here is not a purchase — it is entry into a world.
          </p>
        </div>
      </section>

      {/* THE CHAPTERS — preview grid with locked treatments */}
      <section className="section-dark py-32 md:py-40 relative">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-16 gap-8">
            <div>
              <p className="text-[10px] tracking-luxury uppercase text-gold mb-6">The Archive</p>
              <h2 className="font-editorial text-5xl md:text-7xl leading-[1] tracking-tight">The Chapters</h2>
            </div>
            <div className="max-w-md text-ivory/70 text-sm leading-relaxed">
              Eleven chapters — one identity. Every category is a considered study in silhouette, fabric and mood.
              <div className="mt-6"><Link to="/chapters" className="tracking-widest-plus text-[11px] uppercase text-goldDark link-draw">All chapters →</Link></div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-ivory/10">
            {chapters.slice(0, 10).map((c, i) => {
              const chProducts = productsByChapter[c.roman] || [];
              const hasFaces = !!(c.male_face && c.female_face);
              const hasArt = !hasFaces && c.revealed && !c.restricted && chProducts.length > 0;
              const revealedNoArt = !hasFaces && c.revealed && !c.restricted && chProducts.length === 0;
              return (
                <Link
                  key={c.roman}
                  data-testid={`home-chapter-${c.roman}`}
                  to={`/chapters/${c.roman}`}
                  className="group aspect-[3/4] relative overflow-hidden hover-shift animate-fade-up"
                  style={{ animationDelay: `${i * 40}ms`, background: "#efe7d3" }}
                >
                  {hasFaces ? (
                    <>
                      <div className="absolute inset-0 grid grid-cols-2">
                        <img src={c.male_face}   alt={`Chapter ${c.roman} — ${c.name} · Him`} className="w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-1000" />
                        <img src={c.female_face} alt={`Chapter ${c.roman} — ${c.name} · Her`} className="w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-1000" />
                      </div>
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(43,31,20,0.55) 0%, rgba(43,31,20,0.08) 45%, transparent 100%)" }} />
                      <div className="absolute inset-0 flex flex-col items-center justify-end text-center p-6 z-10 text-ivory">
                        <ChapterEmblem roman={c.roman} size={40} stroke="#f4ecd8" />
                        <p className="font-editorial text-4xl md:text-5xl mt-3 leading-none">{c.roman}</p>
                        <p className="text-[10px] tracking-widest-plus uppercase opacity-90 mt-2">{c.name}</p>
                      </div>
                    </>
                  ) : hasArt ? (
                    <>
                      <img src={chProducts[0].images?.[0]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:opacity-100 transition-opacity duration-700" />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(43,31,20,0.4), transparent 60%)" }} />
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 z-10 text-ivory">
                        <ChapterEmblem roman={c.roman} size={48} stroke="#f4ecd8" />
                        <p className="font-editorial text-4xl md:text-5xl mt-4">{c.roman}</p>
                        <p className="text-[10px] tracking-widest-plus uppercase opacity-90 mt-2">{c.name}</p>
                      </div>
                    </>
                  ) : revealedNoArt ? (
                    <div className="relative w-full h-full">
                      <LockedChapter roman={c.roman} showLabel={false} />
                      <div className="absolute bottom-4 left-0 right-0 text-center text-[10px] tracking-widest-plus uppercase text-[#5a4526]/80">{c.name}</div>
                    </div>
                  ) : (
                    <LockedChapter roman={c.roman} restricted={c.restricted} />
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PIECES */}
      <section className="bg-ivory text-ink py-32 md:py-40">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Objects</p>
              <h2 className="font-editorial text-5xl md:text-6xl leading-[1] tracking-tight">Selected pieces</h2>
            </div>
            <Link to="/collections" className="tracking-widest-plus text-[11px] uppercase link-draw">All collections →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-16">
            {products.slice(0, 6).map((p, i) => (
              <Link key={p.id} to={`/product/${p.slug}`} data-testid={`home-product-${p.slug}`} className="group animate-fade-up relative block" style={{ animationDelay: `${i * 60}ms` }}>
                <ProductImageTile src={p.images[0]} hoverSrcs={p.images.slice(1)} alt={p.name} />
                <WaitlistButton product={p} />
                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] tracking-widest-plus uppercase text-gold mb-2">Chapter {p.chapter_roman}</p>
                    <p className="font-editorial text-xl leading-tight">{p.name}</p>
                    <p className="text-[10px] tracking-widest-plus uppercase text-black/50 mt-2">
                      {p.availability === "in_store" ? "Available in store" : p.availability === "pre_order" ? "Pre-order" : "Coming soon"}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CAMPAIGN feature — uses OFFICIAL provided imagery */}
      {campaigns[0] && (
        <section className="relative h-[80vh] bg-ink text-ivory overflow-hidden">
          <img src={campaigns[0].hero_image} alt={campaigns[0].title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
          <div className="grain" />
          <div className="relative h-full flex flex-col justify-end px-8 md:px-16 pb-16 max-w-[1400px] mx-auto">
            <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Campaign · Chapter {campaigns[0].chapter_roman}</p>
            <h2 className="font-editorial text-5xl md:text-8xl leading-[0.95] tracking-tight max-w-3xl">{campaigns[0].title}</h2>
            <p className="mt-4 font-editorial-italic text-xl md:text-3xl text-ivory/80">{campaigns[0].subtitle}</p>
            <Link to={`/campaigns/${campaigns[0].slug}`} className="mt-10 inline-flex items-center gap-3 tracking-widest-plus text-[11px] uppercase border-b border-ivory/60 pb-2 hover:text-gold hover:border-gold transition-colors w-fit">
              View campaign <ArrowUpRight className="w-4 h-4" strokeWidth={1} />
            </Link>
          </div>
        </section>
      )}

      {/* FOUNDER teaser — official portrait */}
      <section className="bg-parchment text-ink py-24 md:py-36">
        <div className="max-w-[1200px] mx-auto px-8 md:px-16 grid md:grid-cols-2 gap-16 items-center">
          <img src={FOUNDER_IMG} alt="Mohammad Firoz — Founder, LOOXS Fashion" className="w-full aspect-[4/5] object-cover" />
          <div>
            <p className="text-[10px] tracking-luxury uppercase text-gold mb-6">The Founder</p>
            <h2 className="font-editorial text-4xl md:text-6xl leading-[1.05] tracking-tight">Mohammad Firoz.</h2>
            <div className="rule-gold w-16 my-8" />
            <p className="text-lg leading-relaxed text-ink/80 font-light max-w-md">
              LOOXS is the vision of Mohammad Firoz — a house built on the idea that luxury should feel personal, quality should be uncompromised, and fashion should empower individuals to express their best selves every single day.
            </p>
            <Link to="/founder" className="mt-10 inline-block tracking-widest-plus text-[11px] uppercase link-draw">Read the portrait →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
